import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { CsvUploadHistory } from '@ebalance/proto/bffuser/v1/csv_upload_history_model_pb'
import { UploadResult } from '@ebalance/proto/bffuser/v1/csv_upload_history_model_pb'
import { useTranslation } from 'react-i18next'

import { Box, Flex, Text } from '@enechain/ecloud-designsystem'

import { Modal } from '~/components/common/Modal'
import { formatDateTimeFull } from '~/models/dateTime'
import { dataTypeLabelKey } from '~/pages/AdminUploadHistory/models/dataType'

type Props = {
  isOpen: boolean
  onClose: () => void
  history: CsvUploadHistory | undefined
  /** 投入者の表示名 (ListMembers 解決済み、 不在なら id フォールバック)。 */
  uploaderName: string
}

type DetailRowProps = {
  label: string
  value: string
  /** 値の改行 (`\n`) を表示に残す。 */
  preserveLineBreaks?: boolean
}

const DetailRow = ({
  label,
  value,
  preserveLineBreaks = false,
}: DetailRowProps): React.ReactElement => (
  <Flex borderColor="Border.lowEmphasis" borderBottomWidth={1}>
    <Box
      flexShrink={0}
      width="160px"
      background="Surface.tableHeader"
      paddingX={3}
      paddingY={1.5}
    >
      <Text textStyle="smallText" color="Text.midEmphasis">
        {label}
      </Text>
    </Box>
    <Box flex={1} paddingX={3} paddingY={1.5}>
      <Text
        textStyle="smallText"
        whiteSpace={preserveLineBreaks ? 'pre-line' : undefined}
      >
        {value}
      </Text>
    </Box>
  </Flex>
)

/**
 * アップロード履歴の詳細モーダル (docs/specs/admin/data/uploads/history.md AC-004)。
 *
 * TODO(#3648): history.md AC-004/005/006 の縮退・読み替えは PdM 確認待ち。
 * spec は当時の差分プレビュー (行単位の新規/変更/据え置き内容) 再表示を求める
 * が、csv_upload_history は件数サマリーのみ保存する設計 (行レベル差分は保存
 * しない) のため、本モーダルは件数サマリー (成功時) / エラー要因 (失敗時) の
 * 表示に縮退する。
 */
export const DetailModal = ({
  isOpen,
  onClose,
  history,
  uploaderName,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()

  if (history === undefined) {
    return null
  }

  const isFailed = history.result === UploadResult.FAILED

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title={t('page:adminUploadHistory.detail.title')}
    >
      <Box>
        <DetailRow
          label={t('page:adminUploadHistory.column.uploadedAt')}
          value={
            history.uploadedAt !== undefined
              ? formatDateTimeFull(timestampDate(history.uploadedAt))
              : ''
          }
        />
        <DetailRow
          label={t('page:adminUploadHistory.column.uploadedBy')}
          value={uploaderName}
        />
        <DetailRow
          label={t('page:adminUploadHistory.column.dataType')}
          value={t(dataTypeLabelKey(history.dataType))}
        />
        <DetailRow
          label={t('page:adminUploadHistory.column.result')}
          value={
            isFailed
              ? t('page:adminUploadHistory.result.failed')
              : t('page:adminUploadHistory.result.success')
          }
        />
        {isFailed ? (
          // 入札 CSV の確定拒否は「行 N: …」を改行でつないで記録する (#8965)
          <DetailRow
            label={t('page:adminUploadHistory.detail.errorReason')}
            preserveLineBreaks
            value={history.errorReason}
          />
        ) : (
          <>
            <DetailRow
              label={t('page:adminUploadHistory.detail.unchangedCount')}
              value={String(history.unchangedCount)}
            />
            <DetailRow
              label={t('page:adminUploadHistory.detail.newCount')}
              value={String(history.newCount)}
            />
            <DetailRow
              label={t('page:adminUploadHistory.detail.changedCount')}
              value={String(history.changedCount)}
            />
            {history.deletedCount > 0 && (
              <DetailRow
                label={t('page:adminUploadHistory.detail.deletedCount')}
                value={String(history.deletedCount)}
              />
            )}
          </>
        )}
      </Box>
    </Modal>
  )
}
